// Buscador rápido de productos (F3): SKU, nombre o código de barras, en el servidor y PAGINADO (D-UI-2),
// con filtros de familia (categoría) y marca y orden por nombre o SKU. Muestra lo que
// dice el backend: presentaciones con su factor, precio de lista VIGENTE (referencia: el precio de
// la nota lo decide el backend al calcular) y stock físico / reservado / DISPONIBLE por ubicación.
import { useMemo, useState } from 'react';
import {
  useBuscarProductos,
  useCategorias,
  useDebounce,
  useDisponibilidad,
  useMarcas,
  usePrecioReferencia,
  usePresentaciones,
  type FiltroProductos,
} from '../api/consultas';
import type { Presentacion, ProductoEncontrado, Ubicacion } from '../api/contratos';
import { Modal } from '../comun/Modal';
import { decimal, pesos } from '../comun/formato';
import { PanelError } from '../comun/Estados';

export interface ProductoElegido {
  producto: ProductoEncontrado;
  /** La presentación elegida (la caja solo es ayuda de digitación: la línea va en `base`). */
  presentacion: Presentacion;
  base: Presentacion;
}

function PrecioRef({
  empresaId,
  listaId,
  p,
}: {
  empresaId: string;
  listaId: string | null;
  p: Presentacion;
}) {
  const q = usePrecioReferencia(empresaId, listaId, p.producto_id, p.id);
  if (!listaId) return <span className="apagado">sin lista</span>;
  if (q.isPending) return <span className="apagado">…</span>;
  if (q.isError) return <span className="apagado">no disponible</span>;
  if (!q.data) return <span className="insignia tono-malo">sin precio</span>;
  return (
    <span>
      {pesos(q.data.valor)}
      {q.data.escala && <span className="apagado"> · escala</span>}
    </span>
  );
}

/** Stock por ubicación de UN producto (unidad base). La cifra que manda para vender: DISPONIBLE. */
export function TablaStock({
  empresaId,
  productoId,
  ubicaciones,
  resaltar,
  unidad,
}: {
  empresaId: string;
  productoId: string;
  ubicaciones: Ubicacion[];
  resaltar?: string | undefined;
  unidad: string;
}) {
  const q = useDisponibilidad(empresaId, { productoId });
  const nombre = (id: string) => {
    const u = ubicaciones.find((x) => x.id === id);
    return u ? `${u.codigo} · ${u.nombre}${u.tipo ? ` (${u.tipo})` : ''}` : id.slice(0, 8);
  };
  if (q.isError) return <PanelError error={q.error} />;
  return (
    <table className="grilla compacta" aria-label="Stock por ubicación">
      <thead>
        <tr>
          <th>Ubicación</th>
          <th className="al-der">Físico</th>
          <th className="al-der">Reservado</th>
          <th className="al-der">Disponible ({unidad})</th>
        </tr>
      </thead>
      <tbody>
        {(q.data ?? []).map((f) => (
          <tr
            key={f.ubicacionId}
            className={f.ubicacionId === resaltar ? 'fila-resaltada' : undefined}
          >
            <td>{nombre(f.ubicacionId)}</td>
            <td className="al-der mono">{decimal(f.fisico)}</td>
            <td className="al-der mono">{decimal(f.reservado)}</td>
            <td className={`al-der mono fuerte ${f.sobrecomprometido ? 'texto-malo' : ''}`}>
              {decimal(f.disponible)}
              {f.sobrecomprometido && ' ⚠'}
            </td>
          </tr>
        ))}
        {q.isSuccess && !q.data.length && (
          <tr>
            <td colSpan={4} className="grilla-vacia">
              Sin stock registrado en ninguna ubicación
            </td>
          </tr>
        )}
        {q.isPending && (
          <tr>
            <td colSpan={4} className="grilla-vacia">
              Cargando stock…
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export function BuscadorProducto({
  empresaId,
  listaId,
  ubicacionId,
  ubicaciones,
  textoInicial = '',
  preferirFactor = null,
  paraCompra = false,
  alCrear,
  alElegir,
  alCerrar,
}: {
  empresaId: string;
  listaId: string | null;
  ubicacionId: string;
  ubicaciones: Ubicacion[];
  textoInicial?: string;
  /** Compras: Enter elige la presentación de este factor (la caja que trae el XML), si existe. */
  preferirFactor?: number | null;
  /** Compras: se ofrecen las presentaciones que se compran, no las que se venden. */
  paraCompra?: boolean;
  /** Compras: «Crear producto nuevo» con lo escrito, cuando el producto no existe. */
  alCrear?: (texto: string) => void;
  alElegir: (e: ProductoElegido) => void;
  alCerrar: () => void;
}) {
  const [texto, setTexto] = useState(textoInicial);
  const [sel, setSel] = useState(0);
  const [filtro, setFiltro] = useState<FiltroProductos>({ orden: 'nombre' });
  const q = useBuscarProductos(empresaId, useDebounce(texto), filtro);
  const categorias = useCategorias();
  const marcas = useMarcas();
  const filas = useMemo(() => q.filas.filter((p) => p.vendible !== false), [q.filas]);
  const cambiarFiltro = (f: Partial<FiltroProductos>) => {
    setFiltro((x) => ({ ...x, ...f }));
    setSel(0);
  };
  const actual = filas[sel] ?? null;
  // Las presentaciones se piden cuando la selección se DETIENE: recorrer la lista con ↓ no dispara
  // un pedido por fila (con ↓ apretado agotaba el límite de la API). Mientras las que hay en pantalla
  // sean de OTRO producto, no se ofrece ninguna: Enter nunca toma la presentación del anterior.
  const presDe = useDebounce(actual?.id ?? null, 150);
  const pres = usePresentaciones(empresaId, presDe);
  const vigentes = presDe !== null && presDe === actual?.id ? (pres.data ?? []) : [];
  const vendibles = vigentes.filter(
    (p) => (paraCompra ? p.comprable !== false : p.vendible) && p.activa,
  );
  const base =
    (preferirFactor ? vendibles.find((p) => Number(p.factor_a_base) === preferirFactor) : null) ??
    vendibles.find((p) => p.es_base) ??
    vendibles[0] ??
    null;
  // Disponible en la ubicación de la nota, UNA consulta para toda la lista de resultados.
  const stock = useDisponibilidad(empresaId, { ubicacionId });
  const dispDe = (productoId: string) =>
    stock.data?.find((f) => f.productoId === productoId) ?? null;

  const elegir = (p: Presentacion | null) => {
    const b = vigentes.find((x) => x.es_base && (paraCompra || x.vendible) && x.activa) ?? null;
    if (actual && p && b) alElegir({ producto: actual, presentacion: p, base: b });
  };

  return (
    <Modal titulo="Buscar producto (F3)" alCerrar={alCerrar} ancho="ancho">
      <div className="fila-datos filtros-producto">
        <input
          data-autofoco
          className="buscador"
          placeholder="SKU, nombre o código de barras…"
          aria-label="Buscar producto"
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              if (sel >= filas.length - 1) q.cargarMas();
              setSel((s) => Math.min(filas.length - 1, s + 1));
            } else if (e.key === 'ArrowUp') setSel((s) => Math.max(0, s - 1));
            else if (e.key === 'PageDown') q.cargarMas();
            else if (e.key === 'Enter') elegir(base);
            else return;
            e.preventDefault();
          }}
        />
        {categorias.isSuccess && (
          <select
            aria-label="Familia"
            value={filtro.categoriaId ?? ''}
            onChange={(e) => cambiarFiltro({ categoriaId: e.target.value || undefined })}
          >
            <option value="">Todas las familias</option>
            {categorias.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        )}
        {marcas.isSuccess && (
          <select
            aria-label="Marca"
            value={filtro.marcaId ?? ''}
            onChange={(e) => cambiarFiltro({ marcaId: e.target.value || undefined })}
          >
            <option value="">Todas las marcas</option>
            {marcas.data.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
        )}
        <select
          aria-label="Orden de productos"
          value={filtro.orden ?? 'nombre'}
          onChange={(e) => cambiarFiltro({ orden: e.target.value as 'nombre' | 'sku' })}
        >
          <option value="nombre">Por nombre</option>
          <option value="sku">Por SKU</option>
        </select>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <div className="buscador-cuerpo">
        <div
          className="grilla-caja buscador-lista"
          onScroll={(e) => {
            const c = e.currentTarget;
            if (q.hayMas && c.scrollTop + c.clientHeight >= c.scrollHeight - 40) q.cargarMas();
          }}
        >
          <table className="grilla" aria-label="Productos encontrados" aria-busy={q.isFetching}>
            <thead>
              <tr>
                <th style={{ width: '9rem' }}>SKU</th>
                <th>Producto</th>
                <th style={{ width: '4rem' }}>U. base</th>
                <th className="al-der" style={{ width: '6rem' }}>
                  Físico
                </th>
                <th className="al-der" style={{ width: '6rem' }}>
                  Reservado
                </th>
                <th className="al-der" style={{ width: '7rem' }}>
                  Disponible
                </th>
              </tr>
            </thead>
            <tbody>
              {filas.map((p, i) => {
                const d = dispDe(p.id);
                return (
                  <tr
                    key={p.id}
                    aria-selected={i === sel}
                    className={i === sel ? 'fila-sel' : undefined}
                    ref={i === sel ? (el) => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
                    onMouseMove={(e) => {
                      // Solo un movimiento REAL del mouse elige (ver BuscadorCliente).
                      if (e.movementX || e.movementY) setSel(i);
                    }}
                    onDoubleClick={() => elegir(base)}
                  >
                    <td className="mono">{p.sku}</td>
                    <td>{p.nombre}</td>
                    <td>{p.unidad_base_codigo}</td>
                    <td className="al-der mono">{d ? decimal(d.fisico) : '0'}</td>
                    <td className="al-der mono">{d ? decimal(d.reservado) : '0'}</td>
                    <td className="al-der mono fuerte">{d ? decimal(d.disponible) : '0'}</td>
                  </tr>
                );
              })}
              {!filas.length && (
                <tr>
                  <td colSpan={6} className="grilla-vacia">
                    {texto.trim().length < 2 && !filtro.categoriaId && !filtro.marcaId
                      ? 'Escriba al menos 2 caracteres o elija familia / marca'
                      : q.isFetching
                        ? 'Buscando…'
                        : 'Sin productos vendibles con ese criterio'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {filas.length > 0 && (
            <div className="grilla-pie">
              <span className="apagado" data-conteo>
                {filas.length} {q.hayMas ? 'mostrados · hay más' : 'en total'}
              </span>
              {q.hayMas && (
                <button type="button" disabled={q.cargandoMas} onClick={q.cargarMas}>
                  {q.cargandoMas ? 'Cargando…' : 'Cargar más (↓ al final)'}
                </button>
              )}
            </div>
          )}
        </div>
        {actual && (
          <aside className="buscador-detalle" aria-label="Detalle del producto">
            <h3>
              {actual.sku} · {actual.nombre}
            </h3>
            <table className="grilla compacta" aria-label="Presentaciones">
              <thead>
                <tr>
                  <th>Presentación</th>
                  <th className="al-der">Factor</th>
                  <th className="al-der">Precio lista</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {vendibles.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.nombre} <span className="apagado">({p.codigo})</span>
                      {p.es_base && <span className="insignia tono-info">base</span>}
                    </td>
                    <td className="al-der mono">{decimal(p.factor_a_base)}</td>
                    <td className="al-der mono">
                      <PrecioRef empresaId={empresaId} listaId={listaId} p={p} />
                    </td>
                    <td>
                      <button type="button" className="boton-mini" onClick={() => elegir(p)}>
                        Agregar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="ayuda">
              Precio de lista vigente, solo referencia: el precio de la nota (escala D-50, manual
              D-60) lo calcula el backend.
            </p>
            <TablaStock
              empresaId={empresaId}
              productoId={actual.id}
              ubicaciones={ubicaciones}
              resaltar={ubicacionId}
              unidad={actual.unidad_base_codigo}
            />
          </aside>
        )}
      </div>
      <p className="ayuda">
        ↑ ↓ elegir · ↓ al final o AvPág traen más · Enter agrega en unidad base · «Agregar» en una
        caja agrega su equivalente en unidad base · Esc cerrar
      </p>
      {alCrear && (
        <div className="acciones-modal">
          <span className="apagado">¿No está?</span>
          <button type="button" onClick={() => alCrear(texto.trim())}>
            Crear producto nuevo
          </button>
        </div>
      )}
    </Modal>
  );
}
